import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { Edit as EditIcon, Warning as WarningIcon, ArrowDropDown as ArrowDropDownIcon } from '@mui/icons-material';
import { useFirm } from '../../contexts/FirmContext';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '@taxassist/api-client';

interface FirmSelectorProps {
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  disabled?: boolean;
  showLabel?: boolean;
  className?: string;
}

/**
 * FirmSelector provides a dropdown to select the current firm.
 * It automatically integrates with the FirmContext for state management.
 */
export function FirmSelector({ 
  disabled = false,
  className 
}: FirmSelectorProps) {
  const { 
    currentFirmId, 
    availableFirms, 
    setCurrentFirmId, 
    isLoading, 
    error 
  } = useFirm();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);


  const handleFirmSelect = (firmId: string) => {
    setCurrentFirmId(firmId);
    setIsDialogOpen(false);

    const path = location.pathname;

    // If we are on a job details path like /.../jobs/some-id
    const jobPathRegex = /^(.*\/jobs)\/[a-zA-Z0-9-]+$/;
    const match = path.match(jobPathRegex);

    if (match) {
      const basePath = match[1]; // This will be everything up to and including '/jobs'
      navigate(basePath);
    }
  };
  
interface FirmCreditDisplayProps {
  totalCredits: number;
  expiringCredits?: number;
}

// Helper component to display credit data from firm object (no API call needed)
const FirmCreditDisplay = ({ firm }: { firm: FirmCreditDisplayProps }) => {
  const getCreditColor = (credits: number) => {
    if (credits === 0) return 'error.main';
    if (credits < 10) return 'warning.main';
    return 'success.main';
  };
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography 
        variant="caption" 
        sx={{ 
          fontFamily: 'monospace',
          color: getCreditColor(firm.totalCredits),
          fontWeight: 600
        }}
      >
        {firm.totalCredits} credits
      </Typography>
      {firm.expiringCredits && firm.expiringCredits > 0 && (
        <WarningIcon 
          sx={{ 
            fontSize: 14, 
            color: 'warning.main',
            ml: 0.5
          }} 
          titleAccess={`${firm.expiringCredits} credits expiring soon`}
        />
      )}
    </Box>
  );
};

  const getCurrentFirmName = () => {
    const currentFirm = availableFirms.find(firm => firm.firmId === currentFirmId);
    return currentFirm?.name || 'Select Firm';
  };

  // Check if user is admin/reviewer with access to multiple firms
  const isAdminWithMultipleFirms = (user?.role === UserRole.admin || user?.role === UserRole.reviewer) && availableFirms.length > 0;

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading firms...
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Alert severity="error" sx={{ minWidth: 200 }}>
        {error}
      </Alert>
    );
  }

  // Show message if no firms available (but only for non-admin users)
  if (availableFirms.length === 0 && !isAdminWithMultipleFirms) {
    return (
      <Box sx={{ minWidth: 200 }}>
        <Typography variant="body2" color="text.secondary">
          No firms available
        </Typography>
      </Box>
    );
  }

  // Show firm name with edit icon for multiple firms, plain text for single firm
  return (
    <>
      <Box 
        sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }} 
        className={className}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 500,
            color: currentFirmId ? 'text.primary' : 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          {getCurrentFirmName()}
        </Typography>
        
        {(availableFirms.length > 1 || (!currentFirmId && isAdminWithMultipleFirms)) && (
          <IconButton
            size="small"
            onClick={() => setIsDialogOpen(true)}
            disabled={disabled}
            sx={{ 
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' }
            }}
            aria-label="Change firm"
          >
            {!currentFirmId && isAdminWithMultipleFirms ? (
              <ArrowDropDownIcon fontSize="small" />
            ) : (
              <EditIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>

      {/* Firm Selection Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{!currentFirmId ? 'Select Firm to View' : 'Change Firm'}</DialogTitle>
        <DialogContent>
          <List>
            {availableFirms.map((firm) => (
              <ListItem key={firm.firmId} disablePadding>
                <ListItemButton
                  onClick={() => handleFirmSelect(firm.firmId)}
                  selected={firm.firmId === currentFirmId}
                  disabled={!firm.isActive}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body1">{firm.name}</Typography>
                        <FirmCreditDisplay firm={firm} />
                      </Box>
                    }
                    secondary={!firm.isActive ? 'Inactive' : 'Click to view credit details and manage'}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Compact version of FirmSelector for use in headers or toolbars
 */
export function FirmSelectorCompact() {
  return (
    <FirmSelector 
      size="small"
      variant="outlined"
      showLabel={false}
    />
  );
}
